//=============================================================================
//                       <--- Developed By Icon Team --->
//=============================================================================

// npm packages
import React, {
    Fragment,
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

// custom pages
import Table from 'components/Table/Table';
import UnlockShiptoManagementAPI from 'apis/UnlockShiptoManagement/UnlockShiptoManagement';
import Context from 'context/context';
import searchIcon from 'assets/img/Search_Icon.png';
import { styles } from './styles';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { toast } from "react-toastify";

// material-ui
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Badge from '@material-ui/core/Badge';
import Card from '@material-ui/core/Card';
import MenuItem from '@material-ui/core/MenuItem';

// material-ui icons
import Grid from '@material-ui/core/Grid';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import editIcon from "assets/img/editIcon.png";
import * as XLSX from "xlsx";

// Helpers
import useDebounce from 'helpers/useDebounce';
import { blockInvalidChar } from 'helpers/blockInvalidChar';
import * as moment from "moment";
import { CSVLink } from 'react-csv';
import { exportHeaders } from "./export";

// ─── Review Status Color Map ────────────────────────────────────────────────
const REVIEW_STATUS_STYLES = {
    'Eligible':         { color: '#2e7d32',}, // Green
    'Pending Review':   { color: '#f9a825',  }, // Amber
    'Exception Review': { color: '#f9a825' }, // Purple
    'Approved':         { color: '#2e7d32' }, // Blue
    'Rejected':         { color: '#d32f2f' },// Orange Red
    'Ineligible':       { color: '#d32f2f' }, // Red
    'Hard Eligible':    { color: '#2e7d32' }, // Grey
    'Hard Ineligible':  { color: '#d32f2f' }, // Black
};

const ReviewStatusBadge = ({ status }) => {
    const styleMap = REVIEW_STATUS_STYLES[status] || { color: '#9e9e9e' };

    return (
        <span
            style={{
                fontSize: 13,
                fontWeight: 600,
                ...styleMap,
            }}
        >
            {status || '-'}
        </span>
    );
};

// ─── Custom TextField ────────────────────────────────────────────────────────
const CssTextField = withStyles({
    root: {
        '& label.Mui-focused': {
            color: '##D3D3D3',
        },
        '& .MuiFormLabel-root': {
            color: '#707070',
        },
        '& .MuiInputBase-input': {
            color: '#707070',
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: '#707070!important',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: '#707070!important',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#707070',
            },
            '&:hover fieldset': {
                borderColor: '#707070',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#707070',
            },
            '&.MuiOutlinedInput-root': {
                borderRadius: 20,
            },
            '&.MuiOutlinedInput-root': {
                marginTop: 0,
                borderRadius: 20,
                background: '#ededed',
            },
        },
    },
})(TextField);

export default function UnlockShiptoManagement() {
    const { getAPI, searchAPI, Export, upload, filter } = UnlockShiptoManagementAPI();
    const { state, dispatch } = useContext(Context);
    const classes = styles();
    const [data, setData] = useState([]);
    const history = useHistory();
    const [offset, setOffset] = useState(1);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);

    const [searchValue, setSearchValue] = useState('');
    const [exportData, setExportData] = useState([]);
    const { t, i18n } = useTranslation();
    const [response, setResponse] = useState({ filter: false });

    // Filter card visibility
    const [filterCard, setFilterCard] = useState(false);

    const searchBounceValues = useDebounce(searchValue);

    // Common State Object
    const [states, setstates] = useState({
        tier: [],
        reasonDescription: [],
        newTier: "",
        systemManaged: "",
        reason: "",
        editObj: {},
        updateObj: {},
        oldTier: "",
        initiatedBy: "",
        updatedBy: "",
        userDetail: [],
        reviewStatus: "",
        eligibilitySource: "",   // ← NEW
        filter: false,
    });

    const setStates = useCallback((newState) => {
        setstates((states) => ({ ...states, ...newState }));
    }, []);

    // Page Path Name
    useEffect(() => {
        dispatch({
            type: 'SET_PATHNAME',
            payload: ['HOME', 'ADMIN', 'Manage Signature Accounts'],
        });
    }, []);

    // Handling Get, Search and Filter API
    useEffect(() => {
        if (states?.clearSearch) {
            setStates({ clearSearch: false });
            return;
        }

        if (searchBounceValues?.length > 0) {
            handleSearch();
        } else if (states?.filter) {
            handleFilter();
        } else {
            getDetails();
        }
    }, [searchBounceValues, offset, states?.filter]);

    // Pagination handler
    const handlePagination = (value) => {
        console.log("Current Page: ", value);
        setLoading(true);
        setOffset(value);
    };

    // Get API
    async function getDetails() {
        try {
            console.log("Requesting page with offset:", offset);
            setLoading(true);
            let values = {
                offset: (offset - 1) * 10,
                limit: 10,
            };
            dispatch({ type: "SET_LOADING", payload: true });
            let res = await getAPI(values);
            console.log("Data received from backend:", res.data);
            if (res) {
                setData(res.data);
                setCount(res.count);
                setLoading(false);
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } catch (e) {
            console.log("Error fetching data: ", e);
            setLoading(true);
        }
    }

    // Search API
    async function handleSearch() {
        try {
            setLoading(true);
            let values = {
                search: searchValue,
                offset: (offset - 1) * 10,
                limit: 10,
            };
            setResponse({ ...response, filter: false });
            dispatch({ type: "SET_LOADING", payload: true });
            let res = await searchAPI(values);
            if (res) {
                setData(res.data);
                setCount(res.count);
                setLoading(false);
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } catch (e) {
            console.log({ e });
            setLoading(true);
        }
    }

    // Filter API  ← updated to include eligibilitySource
    async function handleFilter() {
        try {
            // Require at least one filter to be selected
            if (!states.reviewStatus && !states.eligibilitySource) return;

            setLoading(true);
            let values = {
                offset: (offset - 1) * 10,
                limit: 10,
                ...(states.reviewStatus    && { status: states.reviewStatus }),
                ...(states.eligibilitySource && { eligibilitySource: states.eligibilitySource }),
            };

            dispatch({ type: "SET_LOADING", payload: true });
            let res = await filter(values);
            if (res) {
                setData(res.data);
                setCount(res.count);
                setFilterCard(false);
                setLoading(false);
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } catch (e) {
            console.log({ e });
            setLoading(true);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }

    // Data Table Headings
    let tableHeading = [
        'Billto',
        'Shipto',
        'Practice Name',
        'Current Thrive Status',
        'Unlocked Program Enrollment',
        'Sales Division',
        'Market Class',
        'Segment',
        'Eligibility Source',
        'Review Status',
        'Created By',
        //'View',
    ];


    const downloadExcel = (data) => {

    // Sheet 1
    // Account Sales Status = Active
    const unlockedEligibility = data
        .filter(item =>
            String(item.account_sales_status || "").toLowerCase() === "active"
        )
        .map(item => ({
            "Billto": item.customer_billto_account_number || "-",
            "Shipto": item.customer_account_number || "-",
            "Practice Name": item.practice_name || "-",
            "Current Thrive Status": item.program_type || "-",
             "Sales Division": item.thrive_sales_division_code || "-",
            "Market Class": item.thrive_market_class_code || "-",
            "Segment": item.thrive_segment_code || "-",
            "System Managed": item.system_managed || "-",
            "Program Admin Reason Description": item.reason_description || item.reason || "-",
            "Unlocked Program Enrollment": item.program_enrollment || "-",
            "Account Sales Status": item.account_sales_status || "-",
            "Added By": item.added_by || item.user_id || "-",
            "Added Date": item.added_date || "-",
            "Eligibility Source": item.eligibility_source || "-",
            "Eligibility Last Evaluated Date": item.eligibility_last_evaluated_date || "-",
            "Review Status": item.review_status || "-",
            "First Updated Date": item.first_updated_date || "-",
            "First Updated By": item.first_updated_by || "-",
            "Last Updated Date": item.updated_date || "-",
            "Last Updated By": item.updated_by || "-",
            "Prior 365-Day Eligible Sales": item.prior_365_day_eligible_sales || "-"
        }));


        // Sheet 2 - Column Descriptions
const columnDescriptions = [
    {
        "#": 1,
        "Columns and its Values": "Billto",
        "Description": "Billto number"
    },
    {
        "#": 2,
        "Columns and its Values": "Shipto",
        "Description": "Shipto Number"
    },
    {
        "#": 3,
        "Columns and its Values": "Practice Name",
        "Description": "Name of the respective Shipping account"
    },
    {
        "#": 4,
        "Columns and its Values": "Current Thrive Status",
        "Description": ""
    },
    {
        "#": "a",
        "Columns and its Values": "Thrive",
        "Description": "This is a Thrive rewards enrolled member"
    },
    {
        "#": "b",
        "Columns and its Values": "Thrive Signature",
        "Description": "This is a Thrive Signature subscribed member"
    },
    {
        "#": "c",
        "Columns and its Values": "Thrive Startup",
        "Description": "This is a Thrive Startup subscribed member"
    },
    {
        "#": "d",
        "Columns and its Values": "Thrive Unlock",
        "Description": "This is a Thrive Unlock subscribed member"
    },
    {
        "#": 5,
        "Columns and its Values": "Sales Division",
        "Description": "This is the Sales Division Code. Eg: (DSL,DSM)"
    },
    {
        "#": 6,
        "Columns and its Values": "Market Class",
        "Description": "This is the Market Class Code. Eg: (PVTPRC,MIDMKT)"
    },
    {
        "#": 7,
        "Columns and its Values": "Segment",
        "Description": "This is the Segment Code. Eg: (PP,PGP,EMRGDSO)"
    },
    {
        "#": 8,
        "Columns and its Values": "System Managed",
        "Description": "This is the System Managed Status. Eg: (Yes/Temp/No)"
    },
    {
        "#": 9,
        "Columns and its Values": "Unlocked Program Enrollment",
        "Description": ""
    },
    {
        "#": "a",
        "Columns and its Values": "Enrolled",
        "Description": "This customer is enrolled in Thrive Unlocked program"
    },
    {
        "#": "b",
        "Columns and its Values": "Not Enrolled",
        "Description": "This customer is not enrolled in Thrive Unlocked program"
    },
    {
        "#": "c",
        "Columns and its Values": "Not Eligible",
        "Description": "This customer is enrolled in other subscrption programs than Thrive Unlocked"
    },
    {
        "#": 10,
        "Columns and its Values": "Account Sales Status",
        "Description": ""
    },
    {
        "#": "a",
        "Columns and its Values": "Active ",
        "Description": "This is an active account in Customer Account Table(master table)"
    },
    {
        "#": "b",
        "Columns and its Values": "Inactive",
        "Description": "This is an Inactive account in Customer Account Table(master table)"
    },
    {
        "#": 11,
        "Columns and its Values": "Added By",
        "Description": "Account created by System/Admin user's email id"
    },
    {
        "#": 12,
        "Columns and its Values": "Added Date",
        "Description": "Account creation date"
    },
    {
        "#": 13,
        "Columns and its Values": "Eligibility Source",
        "Description": ""
    },
    {
        "#": "a",
        "Columns and its Values": "Program Admin",
        "Description": "This account is added by Program admin's in the Master Eligibility Management screen"
    },
    {
        "#": "b",
        "Columns and its Values": "System",
        "Description": "This account is added by Thirve System in the Master Eligibility Management screen"
    },
    {
        "#": "c",
        "Columns and its Values": "Exception Approval",
        "Description": "This account is not added by System/Program Admin in Master Eligibility Management screen, but added during the Account verifcation process by the Program Admin's."
    },
    {
        "#": 14,
        "Columns and its Values": "Eligibility Last Evaluated Date",
        "Description": "The latest date that checked and updated by System "
    },
    {
        "#": 15,
        "Columns and its Values": "Review Status",
        "Description": ""
    },
    {
        "#": "a",
        "Columns and its Values": "Eligible",
        "Description": "This account is eligible to enroll in Thrive Unlocked program"
    },
    {
        "#": "b",
        "Columns and its Values": "Ineligible",
        "Description": "This account is not eligible to enroll in Thrive Unlocked program"
    },
    {
        "#": "c",
        "Columns and its Values": "Pending Review",
        "Description": "Customers with less than $10K in eligible merchandise sales are directed to the Pending Review path during Thrive Unlocked enrollment."
    },
    {
        "#": "d",
        "Columns and its Values": "Exception Review",
        "Description": "Customers with $10K or more in eligible merchandise sales are directed to the Exception Review path during Thrive Unlocked enrollment."
    },
    {
        "#": "e",
        "Columns and its Values": "Hard Eligible",
        "Description": "These accounts are overridded by Program Admin's and make eligible for the Thrive Unlocked program and system will not update the review status for 1 year from the date of addition in to the Master Eligibility Management Screen."
    },
    {
        "#": "f",
        "Columns and its Values": "Hard Ineligible",
        "Description": "These accounts are overridded by Program Admin's and make Ineligible for the Thrive Unlocked program and system will not update the review status for 1 year from the date of addition in to the Master Eligibility Management Screen."
    },
    {
        "#": "g",
        "Columns and its Values": "Approved",
        "Description": "This account is successfully enrolled in to the Thrive Unlocked program"
    },
    {
        "#": "h",
        "Columns and its Values": "Rejected",
        "Description": "This account is rejected by Program Admin during Account verification process."
    },
    {
        "#": 16,
        "Columns and its Values": "First Updated Date",
        "Description": "Date when the record was first updated by the System or Program Admin."
    },
    {
        "#": 17,
        "Columns and its Values": "First Updated By",
        "Description": "System/Program Admin who first updated the record."
    },
    {
        "#": 18,
        "Columns and its Values": "Last Updated Date",
        "Description": "Date when the record was most recently updated by the System or Program Admin."
    },
    {
        "#": 19,
        "Columns and its Values": "Last Updated By",
        "Description": "System/Program Admin who most recently updated the record."
    },
    {
        "#": 20,
        "Columns and its Values": "Prior 365-Day Eligible Sales",
        "Description": "System updates this sales amount on a daily basis for the past 365 eligibile merch sales for this account."
    }
];


    // Sheet 2
    // Current Thrive Status = Thrive Startup / Thrive Signature
    // Review Status = Hard Eligible / Hard Ineligible / Ineligible
    const ineligibleReason = data
        .filter(item =>
           [ "Ineligible"].includes(
                String(item.review_status || "").trim()
            )
        )
        .map(item => ({
            "Billto": item.customer_billto_account_number || "-",
            "Shipto": item.customer_account_number || "-",
            "Practice Name": item.practice_name || "-",
            "Current Thrive Status": item.program_type || "-",
             "Sales Division": item.thrive_sales_division_code || "-",
            "Market Class": item.thrive_market_class_code || "-",
            "Segment": item.thrive_segment_code || "-",
            "System Managed": item.system_managed || "-",
            "Program Admin Reason Description": item.reason_description || item.reason || "-",
            "Unlocked Program Enrollment": item.program_enrollment || "-",
            "Review Status": item.review_status || "-",
            "Last Updated At": item.updated_date || "-",
            "Prior 365": item.prior_365_day_eligible_sales || "-"
        }));


    // Sheet 3
    // Account Sales Status = Inactive
    // Review Status = Hard Ineligible / Ineligible
    const inactiveLocations = data
        .filter(item =>
            String(item.account_sales_status || "").toLowerCase() === "inactive"
        )
        .map(item => ({
            "Billto": item.customer_billto_account_number || "-",
            "Shipto": item.customer_account_number || "-",
            "Practice Name": item.practice_name || "-",
            "Current Thrive Status": item.program_type || "-",
            "Sales Division": item.thrive_sales_division_code || "-",
            "Market Class": item.thrive_market_class_code || "-",
            "Segment": item.thrive_segment_code || "-",
            "System Managed": item.system_managed || "-",
            "Program Admin Reason Description": item.reason_description || item.reason || "-",
            "Unlocked Program Enrollment": item.program_enrollment || "-",
            "Account Sales Status": item.account_sales_status || "-",
            "Added By": item.added_by || item.user_id || "-",
            "Added Date": item.added_date || "-",
            "Eligibility Source": item.eligibility_source || "-",
            "Eligibility Last Evaluated Date": item.eligibility_last_evaluated_date || "-",
            "Review Status": item.review_status || "-",
             "First Updated Date": item.first_updated_date || "-",
            "First Updated By": item.first_updated_by || "-",
            "Last Updated Date": item.updated_date || "-",
            "Last Updated By": item.updated_by || "-",
            "Prior 365-Day Eligible Sales": item.prior_365_day_eligible_sales || "-"
        }));


    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Create sheets
    const sheet1 = XLSX.utils.json_to_sheet(unlockedEligibility);
    const sheet2 = XLSX.utils.json_to_sheet(columnDescriptions);
    const sheet3 = XLSX.utils.json_to_sheet(ineligibleReason);
    const sheet4 = XLSX.utils.json_to_sheet(inactiveLocations);

    // Add sheets
    XLSX.utils.book_append_sheet(
        workbook,
        sheet1,
        "Unlocked_Eligibility_Management"
    );



XLSX.utils.book_append_sheet(
    workbook,
    sheet2,
    "Column Descriptions"
);

    XLSX.utils.book_append_sheet(
        workbook,
        sheet3,
        "Ineligible Reason"
    );

    XLSX.utils.book_append_sheet(
        workbook,
        sheet4,
        "Inactive Locations"
    );

    // Download
    XLSX.writeFile(
        workbook,
        `Unlocked_Eligibility_Management_${moment(new Date()).format(
            "MM-DD-YYYY,h-mm A"
        )}.xlsx`
    );
};


const handleExport = async () => {
    try {
        let values = { searchValue };

        setExportLoading(true);
        dispatch({ type: "SET_LOADING", payload: true });

        let res = await Export(values);

        if (res && res.data) {

            dispatch({ type: "SET_LOADING", payload: false });
            setExportLoading(false);

            if (res.data.length === 0) {
                toast.success(t("No Data Found"), {
                    toastId: "no-report-date"
                });
                return;
            }

            // Create Excel with 3 sheets
            downloadExcel(res.data);
        }

    } catch (e) {
        console.log({ e });

        setExportLoading(false);
        dispatch({ type: "SET_LOADING", payload: false });
    }
};

    // const handleExport = async () => {
    //     try {
    //         let values = { searchValue };
    //         setExportLoading(true);
    //         dispatch({ type: "SET_LOADING", payload: true });
    //         let res = await Export(values);
    //         if (res && res.data) {
    //             setExportData(res.data);
    //             dispatch({ type: "SET_LOADING", payload: false });
    //             setExportLoading(false);
    //             if (res.data.length === 0) {
    //                 toast.success(t("No Data Found"), { toastId: "no-report-date" });
    //                 return false;
    //             }
    //             return true;
    //         }
    //     } catch (e) {
    //         console.log({ e });
    //         setExportLoading(false);
    //     }
    // };

    // ─── Eligibility Source colour chips (mirrors image) ──────────────────
   const ELIGIBILITY_SOURCE_STYLES = {
    'System': {
        background: '#2e7d32', // Green
        color: '#fff',
    },
    'Program Admin': {
        background: '#9e9e9e', // Ash/Grey
        color: '#fff',
    },
    'Exception Approval': {
    background: '#90caf9', // Light Blue
    color: '#fff',         // better contrast for light background
},
};

    const EligibilitySourceBadge = ({ source }) => {
        const s = ELIGIBILITY_SOURCE_STYLES[source] || { background: '#9e9e9e', color: '#070707' };
        return (
            <span
                style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...s,
                }}
            >
                {source || '-'}
            </span>
        );
    };

    return (
        <Fragment>
            <Grid item xs={12} sm={12} lg={12} className={classes.topGrid}>
                <div style={{ flex: 1 }}>
                    <CssTextField
                        id="search"
                        placeholder="Search Shipto ID, Billto ID or Practice Name"
                        onKeyDown={blockInvalidChar}
                        type="text"
                        variant="outlined"
                        autoComplete="off"
                        className={classes.searchInput}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchValue.length > 0 ? (
                                        <CloseOutlinedIcon
                                            className={classes.searchInputIcon}
                                            onClick={() => {
                                                setSearchValue("");
                                                if (offset === 1) {
                                                    if (states.filter) {
                                                        handleFilter();
                                                    } else {
                                                        getDetails();
                                                    }
                                                } else {
                                                    setOffset(1);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={searchIcon}
                                            alt=""
                                            width="20px"
                                            className={classes.searchInputIcon}
                                        />
                                    )}
                                </InputAdornment>
                            ),
                        }}
                        value={searchValue}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\s+/g, "");
                            setSearchValue(val);
                            setOffset(1);
                            if (e.target.value.length === 0) {
                                if (states.filter) {
                                    handleFilter();
                                } else {
                                    getDetails();
                                }
                            }
                        }}
                    />
                </div>

                {/* RIGHT SIDE BUTTONS */}
                <Grid
                    item
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    {/* FILTER */}
                    <Badge
                        badgeContent={states.filter && count > 0 ? count : 0}
                        color="error"
                        classes={{ badge: classes.badge }}
                        max={999}
                        invisible={!states.filter || count === 0}
                    >
                        <Button
                            className={`${classes.filterBtn} dash-1-filter-btn`}
                            onClick={() => {
                                setFilterCard(!filterCard);
                                if (!states.filter) {
                                    setStates({ reviewStatus: "", eligibilitySource: "" });
                                }
                            }}
                        >
                            {t('Filter')}
                        </Button>
                    </Badge>

                    {/* CREATE */}
                    <button
                        className={classes.uploadBtn}
                        onClick={() => history.push("/dashboard/unlock-create-shipto")}
                    >
                        {t("Add/Edit Customer")}
                    </button>

                    {/* UPLOAD */}
                    <button
                        className={classes.uploadBtn}
                        onClick={() => history.push("/dashboard/unlock-upload-shipto")}
                    >
                        {t("Upload Prequalified List")}
                    </button>

                    {/* CSV LINK */}
                    <CSVLink
                        id="csv"
                        data={exportData.map(item => ({
                            ...item,
                            is_active: item.is_active ? "Active" : "Inactive",
                            customer_billto_account_number: item.customer_billto_account_number
                                ? item.customer_billto_account_number : "-",
                            customer_name: item.customer_name
                                ? item.customer_name : "-",
                        }))}
                        headers={exportHeaders}
                        filename={
                            `Unlocked_Eligibility_Management ` +
                            moment(new Date()).format("MM-DD-YYYY,h:mm A") +
                            ".csv"
                        }
                        className={classes.CSVlink}
                    />

                    {/* EXPORT BUTTON */}
                    <button
    disabled={exportLoading}
    className={classes.exportBtn1}
    onClick={async () => {
        const res = await handleExport();
        if (res) {
            document.getElementById("csv").click();
        }
    }}
>
                        {exportLoading ? (
        <div className="snippet" style={{ marginLeft: 65 }}>
            <div className="stage">
                <div className="dot-typing-e"></div>
            </div>
        </div>
    ) : (
        t("Export List")
    )}
                    </button>
                </Grid>
            </Grid>

            {/* ── FILTER BAR ── */}
            {filterCard && (
                <div className={classes.filterCardDiv} style={{ marginTop: 16 }}>
                    <Card
                        className={classes.filterCard}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 12,
                            boxShadow: "none",
                            padding: "12px 20px",
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            className={classes.filterCardGridContainer}
                        >
                            {/* Review Status */}
                            <Grid item xs={12} sm={4} md={3} className={classes.filterCardGrid}>
                                <TextField
                                    id="reviewStatus"
                                    label={t('Review Status')}
                                    select
                                    fullWidth
                                    type="text"
                                    value={states?.reviewStatus}
                                    onChange={(e) => setStates({ reviewStatus: e.target.value })}
                                >
                                    <MenuItem value="Eligible">{t('Eligible')}</MenuItem>
                                    <MenuItem value="Ineligible">{t('Ineligible')}</MenuItem>
                                    <MenuItem value="Hard Eligible">{t('Hard Eligible')}</MenuItem>
                                    <MenuItem value="Pending Review">{t('Pending Review')}</MenuItem>
                                    <MenuItem value="Exception Review">{t('Exception Review')}</MenuItem>
                                    <MenuItem value="Approved">{t('Approved')}</MenuItem>
                                    <MenuItem value="Rejected">{t('Rejected')}</MenuItem>
                                    <MenuItem value="Hard Ineligible">{t('Hard Ineligible')}</MenuItem>
                                </TextField>
                            </Grid>

                            {/* ── NEW: Eligibility Source filter ── */}
                            <Grid item xs={12} sm={4} md={3} className={classes.filterCardGrid}>
                                <TextField
                                    id="eligibilitySource"
                                    label={t('Eligibility Source')}
                                    select
                                    fullWidth
                                    type="text"
                                    value={states?.eligibilitySource}
                                    onChange={(e) => setStates({ eligibilitySource: e.target.value })}
                                >
                                    <MenuItem value="System">{t('System')}</MenuItem>
                                    <MenuItem value="Program Admin">{t('Program Admin')}</MenuItem>
                                    <MenuItem value="Exception Approval">{t('Exception Approval')}</MenuItem>
                                </TextField>
                            </Grid>

                            {/* Buttons */}
                            <Grid
                                item
                                xs={12}
                                sm="auto"
                                className={classes.filterCardBtnGrid}
                                style={{ display: "flex", gap: "10px" }}
                            >
                                <Button
                                    className={classes.filterCardApplyBtn}
                                    onClick={() => {
                                        if (!states.reviewStatus && !states.eligibilitySource) {
                                            toast.error('Please select at least one filter', {
                                                toastId: 'filter-empty',
                                            });
                                            return;
                                        }
                                        setSearchValue('');
                                        setStates({ filter: true });
                                        setOffset(1);
                                        setCount(0);
                                        handleFilter();
                                    }}
                                >
                                    {t('Apply')}
                                </Button>

                                {states.filter && (
                                    <Button
                                        className={classes.filterCardResetBtn}
                                        onClick={() => {
                                            setStates({
                                                filter: false,
                                                reviewStatus: '',
                                                eligibilitySource: '',
                                            });
                                            setFilterCard(false);
                                            if (offset === 1) {
                                                getDetails();
                                            } else {
                                                setOffset(1);
                                            }
                                        }}
                                    >
                                        {t('Reset')}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Card>
                </div>
            )}

            {/* ── DATA TABLE ── */}
            <div style={{ marginTop: 20 }}>
                {!loading ? (
                    <Table
                        headings={tableHeading.map((i) => (
                            <div style={{ textAlign: "center", width: "100%" }} key={t(i)}>
                                {t(i)}
                            </div>
                        ))}
                        data={data?.map((d, i) => [
                            // Billto
                            <div style={{ textAlign: "center" }}>
                                {d?.customer_billto_account_number || "-"}
                            </div>,

                            // Shipto
                            <div style={{ textAlign: "center" }}>
                                {d?.customer_account_number}
                            </div>,

                            // Practice Name
                            <div style={{ textAlign: "center" }}>
                                {d?.practice_name || "-"}
                            </div>,

                            // Current Thrive Status
                            <div style={{ textAlign: "center" }}>
                                {d?.program_type || "-"}
                            </div>,

                            // Unlocked Program Enrollment
                            <div style={{ textAlign: "center" }}>
                                {d?.program_enrollment || "-"}
                            </div>,

                            // Account Sales Status
                            <div style={{ textAlign: "center" }}>
                                <span >
                                    {d?.customer_sales_division_code }
                                </span>
                            </div>,
                             <div style={{ textAlign: "center" }}>
                                <span >
                                    {d?.market_class_code }
                                </span>
                            </div>,
                             <div style={{ textAlign: "center" }}>
                                <span >
                                    {d?.thrive_segment_code }
                                </span>
                            </div>,

                            // Eligibility Source  ← colour chip
                            <div style={{ textAlign: "center" }}>
                                <EligibilitySourceBadge source={d?.eligibility_source} />
                            </div>,

                            // Review Status  ← colour badge
                            <div style={{ textAlign: "center" }}>
                                <ReviewStatusBadge status={d?.review_status} />
                            </div>,

                            // Date Account Added
                            // <div style={{ textAlign: "center" }}>
                            //     {d?.added_date || "-"}
                            // </div>,

                            // Created By
                            <div style={{ textAlign: "center" }}>
                                {d?.user_id}
                            </div>,

                            // // View / Edit
                            // ['Eligible', 'Ineligible', 'Hard Ineligible', 'Inactive'].includes(d?.review_status)
                            //     ? (
                            //         <div
                            //             style={{
                            //                 display: "flex",
                            //                 justifyContent: "center",
                            //                 alignItems: "center",
                            //                 cursor: "pointer",
                            //             }}
                            //             onClick={() => history.push('/dashboard/unlock-edit-shipto', { data: d })}
                            //         >
                            //             <VisibilityIcon />
                            //         </div>
                            //     )
                            //     : (
                            //         <div style={{ textAlign: "center" }}>-</div>
                            //     ),
                        ])}
                        offset={offset}
                        count={Math.ceil(count / 10)}
                        handlePagination={handlePagination}
                    />
                ) : (
                    <div
                        style={{
                            height: '25vh',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <h2>{t('Loading...')}</h2>
                    </div>
                )}
            </div>
        </Fragment>
    );
}
